<template>
  <PopupContainer
    :isSwipeEnabled="false"
    @close="closePopup"
    @blur="closePopup"
    @swipe="(direction) => swipePost(direction)"
    @scroll="(scrollPos) => loadCustomersMediaOnScroll(scrollPos)"
  >
    <template #content="contentWrapper">
      <div class="frisbuy-popup-inner">
        <div class="frisbuy-popup-mobile-header">
          <span>Все фото покупателей</span>
          <span>{{ currentMediaIndex + 1 }}/{{ popupMediaCount }}</span>
        </div>

        <div
          class="frisbuy-popup-content"
          :class="{
            'frisbuy-popup-content--video-playing':
              isMobileViewport && isCurrentMediaVideo && isVideoPlaying,
          }"
        >
          <div class="frisbuy-popup-content__media-column">
            <div class="frisbuy-popup-content__image">
              <Swiper
                class="frisbuy-popup-content__swiper"
                :initial-slide="currentMediaIndex"
                :allow-touch-move="isMobileViewport"
                @swiper="onSwiperInit"
                @slideChange="onSlideChange"
              >
                <SwiperSlide
                  v-for="(media, mediaIndex) in swiperMediaList"
                  :key="`${media.postId}-${media.thumbnailUrl || mediaIndex}`"
                  class="frisbuy-popup-content__slide"
                >
                  <img
                    v-if="!media.videoUrl"
                    :src="
                      DomainSubstitute(
                        media.imageHighResolutionUrl || media.imageUrl
                      )
                    "
                    @error="errorImageHandler"
                  />

                  <img
                    v-else-if="
                      !isVideoStarted || currentMediaIndex !== mediaIndex
                    "
                    :src="
                      DomainSubstitute(
                        media.imageHighResolutionUrl || media.imageUrl
                      )
                    "
                    @error="errorImageHandler"
                  />

                  <video
                    v-if="media.videoUrl"
                    v-show="isVideoStarted && currentMediaIndex === mediaIndex"
                    :src="media.videoUrl"
                    muted
                    loop="true"
                    playsinline
                    :controls="!isMobileViewport && isVideoStarted"
                    :ref="
                      (el) => setVideoRef(el as HTMLVideoElement | null, mediaIndex)
                    "
                    @click.stop.prevent="onVideoClick"
                    @play="onVideoPlay"
                    @pause="onVideoPause"
                  ></video>
                </SwiperSlide>
              </Swiper>

              <button
                v-if="isMediaNavigationVisible && canSlidePrev"
                class="frisbuy-popup-content__button frisbuy-popup-content__button--left"
                @click="selectPrevMedia"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.8991 5.89646C14.2896 5.50599 14.9228 5.50607 15.3133 5.89646C15.7038 6.28699 15.7038 6.92016 15.3133 7.31068L10.6239 12.0001L15.3133 16.6895L15.3823 16.7655C15.7025 17.1582 15.6793 17.7377 15.3133 18.1037C14.9472 18.4696 14.3677 18.4929 13.975 18.1728L13.8991 18.1037L8.71454 12.9192C8.20709 12.4116 8.20718 11.5886 8.71454 11.081L13.8991 5.89646Z"
                    fill="#999999"
                  />
                </svg>
              </button>

              <span
                v-if="currentMedia?.videoUrl && !isVideoStarted"
                class="frisbuy-popup-content__play-icon"
                @click="playVideo"
              >
                <svg
                  width="56"
                  height="56"
                  viewBox="0 0 56 56"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="28"
                    cy="28"
                    r="20"
                    fill="#212121"
                    fill-opacity="0.2"
                  />
                  <g opacity="0.8">
                    <path
                      d="M33.1441 26.4716C34.0828 27.0557 34.0828 28.5299 33.1441 29.114L26.3015 33.3723C25.37 33.9521 24.2109 33.2195 24.2109 32.0511L24.2109 23.5345C24.2109 22.3661 25.37 21.6335 26.3015 22.2132L33.1441 26.4716Z"
                      fill="white"
                    />
                    <path
                      d="M40.6553 28.0006C40.6553 21.0104 34.9892 15.343 27.999 15.3427C21.0086 15.3427 15.3411 21.0102 15.3411 28.0006C15.3415 34.9908 21.0088 40.6569 27.999 40.6569C34.989 40.6566 40.655 34.9906 40.6553 28.0006ZM43.9886 28.0006C43.9883 36.8315 36.83 43.9899 27.999 43.9902C19.1678 43.9902 12.0081 36.8317 12.0078 28.0006C12.0078 19.1692 19.1676 12.0094 27.999 12.0094C36.8302 12.0097 43.9886 19.1694 43.9886 28.0006Z"
                      fill="white"
                    />
                  </g>
                </svg>
              </span>

              <button
                v-if="isMediaNavigationVisible && canSlideNext"
                class="frisbuy-popup-content__button frisbuy-popup-content__button--right"
                @click="selectNextMedia"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.8991 5.89646C14.2896 5.50599 14.9228 5.50607 15.3133 5.89646C15.7038 6.28699 15.7038 6.92016 15.3133 7.31068L10.6239 12.0001L15.3133 16.6895L15.3823 16.7655C15.7025 17.1582 15.6793 17.7377 15.3133 18.1037C14.9472 18.4696 14.3677 18.4929 13.975 18.1728L13.8991 18.1037L8.71454 12.9192C8.20709 12.4116 8.20718 11.5886 8.71454 11.081L13.8991 5.89646Z"
                    fill="#999999"
                  />
                </svg>
              </button>

              <button
                v-if="
                  isMediaNavigationVisible && canSlidePrev && !isVideoStarted
                "
                class="frisbuy-popup-content__click-zone frisbuy-popup-content__click-zone--left"
                type="button"
                aria-label="Previous media"
                @click="selectPrevMedia"
              ></button>
              <button
                v-if="
                  isMediaNavigationVisible && canSlideNext && !isVideoStarted
                "
                class="frisbuy-popup-content__click-zone frisbuy-popup-content__click-zone--right"
                type="button"
                aria-label="Next media"
                @click="selectNextMedia"
              ></button>
            </div>

            <span
              v-if="isMediaNavigationVisible"
              class="frisbuy-popup-content__media-counter"
            >
              {{ currentMediaIndex + 1 }}/{{ popupMediaCount }}
            </span>
          </div>

          <div
            class="frisbuy-popup-content__bottom-layer"
            :class="{
              'frisbuy-popup-content__bottom-layer--video-playing':
                isMobileViewport && isCurrentMediaVideo && isVideoPlaying,
            }"
          >
            <div
              class="frisbuy-popup-content__info"
              :class="{
                'frisbuy-popup-content__info--expanded':
                  isPopupReadMoreExpanded,
                'frisbuy-popup-content__info--video-interface-visible':
                  isMobileViewport && isCurrentMediaVideo && isVideoPlaying,
              }"
            >
              <CustomerReview
                :customerName="
                  post.instagramPost.authorUser.fio ||
                  `@${post.instagramPost.authorUser.username}`
                "
                :avatarColorIndex="props.selectedPostIndex + 1"
                :reviewDate="props.reviewDate"
                :reviewTime="props.reviewTime"
                :rating="post.frisbuyPost.rating"
                :negativeComment="post.frisbuyPost.negativeComment"
                :positiveComment="post.frisbuyPost.positiveComment"
                :text="post.frisbuyPost.text"
                :isShowMedia="false"
                authorView="popup"
                :sectionsMarginTop="['0', '20px', '30px']"
                @selectMedia="(media) => selectMedia(media)"
                @popupReadMoreToggle="
                  (expanded) => (isPopupReadMoreExpanded = expanded)
                "
              >
                <template #topRight>
                  <button
                    class="frisbuy-popup-content__open-review frisbuy-popup-content__open-review--inline"
                    type="button"
                    @click="isMobileReviewSheetOpen = true"
                  >
                    См. отзыв
                  </button>
                </template>
              </CustomerReview>

              <PopupReviewMobileMediaSlider
                v-if="isMobileViewport && isMobileMediaVisible"
                class="frisbuy-popup-content__modal-media"
                :mediaList="props.post.media"
                :selectedMediaThumbnailUrl="currentPostMediaThumbnailUrl"
                @selectMedia="(media) => onModalMediaSelect(media)"
              />

              <PopupReviewMediaList
                v-if="!isMobileViewport"
                class="frisbuy-popup-content__modal-media"
                :mediaList="props.post.media"
                :selectedMediaThumbnailUrl="currentPostMediaThumbnailUrl"
                @selectMedia="(media) => onModalMediaSelect(media)"
              />
            </div>

            <PopupReviewMobileVideoControls
              v-if="isMobileViewport && isCurrentMediaVideo && isVideoPlaying"
              :videoEl="videoElement"
            />
          </div>
        </div>

        <div
          v-if="props.allCustomersMediaList.length"
          class="frisbuy-popup-customers-reviews"
        >
          <h3>Другие фото из отзывов</h3>

          <div class="frisbuy-popup-media-list">
            <MediaList
              :mediaList="props.allCustomersMediaList"
              :mediaWidth="allCustomersMediaListItemsWidth"
              wrap="wrap"
              @selectMedia="
                (media) => {
                  selectPost(media.postId, media);

                  // NOTE: `nextTick` only for fix Safari
                  nextTick(() => {
                    contentWrapper.scrollToTop();
                  });
                }
              "
            />
          </div>
        </div>
      </div>

      <MobileReviewSheet
        :isOpen="isMobileReviewSheetOpen"
        :post="post"
        :reviewIndex="props.selectedPostIndex + 1"
        :reviewDate="props.reviewDate"
        :selectedMediaThumbnailUrl="currentMedia?.thumbnailUrl"
        @close="isMobileReviewSheetOpen = false"
        @selectMedia="(media) => selectMedia(media)"
      />
    </template>
  </PopupContainer>
</template>

<style scoped lang="scss">
@import "@/widgets/MarketplaceReviews/mixins.scss";

.frisbuy-popup-inner {
  font-family: Gilroy, "Segoe UI", Arial, sans-serif;

  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
    font-family: Gilroy, "Segoe UI", Arial, sans-serif;
  }

  padding: 40px 32px 16px;

  .frisbuy-popup-mobile-header {
    display: none;
  }

  .frisbuy-popup-content {
    .frisbuy-popup-content__bottom-layer {
      display: contents;
      order: 1;
    }

    display: flex;
    align-items: flex-start;
    gap: 28px;
    height: 524px;
    max-height: 524px;
    overflow: visible;

    .frisbuy-popup-content__button {
      top: 50%;
      transform: translateY(-50%);
      z-index: 3;
    }

    .frisbuy-popup-content__info {
      width: 39%;
      min-width: 0;
      height: auto;
      max-height: 500px;
      overflow: hidden;
      padding-right: 4px;
      display: flex;
      flex-direction: column;
      align-items: stretch;
    }

    .frisbuy-popup-content__info:has(.frisbuy-customer-review__read-more) {
      height: 500px;
    }

    .frisbuy-popup-content__modal-media {
      padding-top: 0;
      flex-shrink: 0;
    }

    .frisbuy-popup-content__info :deep(.frisbuy-modal-media-list) {
      display: grid;
      grid-template-columns: repeat(5, 48px);
      gap: 8px;
      width: max-content;
      max-width: none;
      overflow: visible;
    }

    .frisbuy-popup-content__info :deep(.frisbuy-modal-media-list > *) {
      box-sizing: border-box;
      min-width: 0;
    }

    .frisbuy-popup-content__info
      :deep(.frisbuy-modal-media-list__item--selected::after) {
      inset: -4px;
      box-sizing: border-box;
    }

    .frisbuy-popup-content__media-column {
      display: flex;
      flex-direction: column;
      flex: 0 0 500px;
      width: 500px;
      min-width: 500px;
      order: 2;
    }

    .frisbuy-popup-content__info :deep(.frisbuy-customer-review--modal-popup) {
      align-self: start;
      flex: 0 0 auto;
      height: auto;
      min-height: 0;
      overflow-y: visible;
      overflow-x: hidden;
      padding-right: 2px;
      margin-bottom: 24px;
    }

    .frisbuy-popup-content__info:has(.frisbuy-customer-review__read-more)
      :deep(.frisbuy-customer-review--modal-popup) {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
    }

    .frisbuy-popup-content__info
      :deep(
        .frisbuy-customer-review--modal-popup:not(
            :has(.frisbuy-customer-review__read-more)
          )
      ) {
      margin-bottom: 20px;
    }

    .frisbuy-popup-content__info :deep(.frisbuy-customer-review__top-section) {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
    }

    .frisbuy-popup-content__info :deep(.frisbuy-review-author--popup) {
      align-items: flex-start;
      min-width: 0;
      flex: 1 1 auto;
    }

    .frisbuy-popup-content__info :deep(.frisbuy-review-author__meta) {
      gap: 0;
    }

    .frisbuy-popup-content__info :deep(.frisbuy-review-author__name) {
      font-size: 14px;
      line-height: 20px;
      font-weight: 400;
      color: rgba(33, 33, 33, 1);
    }

    .frisbuy-popup-content__info :deep(.frisbuy-review-author__date) {
      font-size: 12px;
      line-height: 16px;
      font-weight: 400;
      color: rgba(153, 153, 153, 1);
    }

    .frisbuy-popup-content__info :deep(.frisbuy-review-author__avatar) {
      font-size: 28px;
      line-height: 28px;
      font-weight: 400;
    }

    .frisbuy-popup-content__info--expanded {
      display: block;
      overflow-y: auto;
      overflow-x: hidden;
    }

    .frisbuy-popup-content__info--expanded
      :deep(.frisbuy-customer-review--modal-popup) {
      overflow: visible;
      padding-right: 0;
    }

    .frisbuy-popup-content__image {
      position: relative;
      height: 500px;
      border-radius: 6px;
      overflow: hidden;
      background: #f4f4f4;

      .frisbuy-popup-content__swiper,
      .frisbuy-popup-content__slide {
        width: 100%;
        height: 100%;
      }

      .frisbuy-popup-content__swiper :deep(.swiper-wrapper) {
        height: 100%;
      }

      img,
      video {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      video {
        position: absolute;
        inset: 0;
        object-fit: contain;
        background: rgba(246, 246, 246, 0.7);
      }

      .frisbuy-popup-content__play-icon {
        position: absolute;
        inset: 50%;
        transform: translate(-50%, -50%);
        width: 56px;
        height: 56px;

        z-index: 3;

        svg {
          width: 56px;
          height: 56px;
        }
      }
    }

    .frisbuy-popup-content__media-counter {
      margin-top: 4px;
      margin-left: auto;
      font-size: 14px;
      line-height: 20px;
      font-weight: 400;
      color: rgba(33, 33, 33, 1);
    }

    .frisbuy-popup-content__info :deep(.frisbuy-customer-review__text) {
      margin-top: 16px;

      & > *:not(:first-child) {
        margin-top: 12px;
      }
    }

    .frisbuy-popup-content__info :deep(.frisbuy-customer-review__read-more) {
      margin-top: 8px;
    }
  }

  .frisbuy-popup-customers-reviews {
    margin-top: 24px;

    h3 {
      font-size: 14px;
      font-weight: 700;
      line-height: 22px;
      color: #303133;
    }

    .frisbuy-popup-media-list {
      margin-top: 10px;
    }
  }

  .frisbuy-popup-content__button {
    display: flex;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border: 0;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
    cursor: pointer;
  }

  .frisbuy-popup-content__click-zone {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 50%;
    border: 0;
    background: transparent;
    z-index: 2;
    cursor: pointer;
  }

  .frisbuy-popup-content__click-zone--left {
    left: 0;
  }

  .frisbuy-popup-content__click-zone--right {
    right: 0;
  }

  .frisbuy-popup-content__button--left {
    left: 12px;
  }

  .frisbuy-popup-content__button--right {
    right: 12px;

    svg {
      transform: scaleX(-1);
    }
  }

  .frisbuy-popup-customers-reviews {
    display: none;
  }

  .frisbuy-popup-content__open-review {
    display: none;
  }

  .frisbuy-popup-content__info :deep(.frisbuy-customer-review__read-more) {
    color: #212121;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    border-bottom-color: #212121;

    &:hover {
      color: #00b9bf;
      border-bottom-color: #00b9bf;
    }
  }
}

@media (max-width: 1024px) {
  .frisbuy-popup-inner {
    background: #1f2328;
    color: #ffffff;
    height: auto;
    min-height: 0;
    overflow: hidden;
    padding: 0;

    .frisbuy-popup-mobile-header {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: 2px;
      min-height: 42px;
      padding: 4px 56px;
      text-align: center;
      width: 100%;

      span:first-child {
        font-size: 11px;
        font-weight: 700;
        line-height: 14px;
      }

      span:last-child {
        font-size: 10px;
        line-height: 12px;
        color: rgba(255, 255, 255, 0.75);
      }
    }

    .frisbuy-popup-content {
      --frisbuy-mobile-video-interface-height: 67px;

      display: block;
      position: relative;
      padding: 0;
      height: auto;
      min-height: calc(100dvh - 42px);
      overflow: hidden;

      .frisbuy-popup-content__button {
        display: none;
      }

      .frisbuy-popup-content__bottom-layer {
        display: block;
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 5;
        order: initial;
      }

      .frisbuy-popup-content__media-column {
        width: 100%;
        min-width: 0;
        height: calc(100dvh - 42px);
        min-height: 0;
        max-height: none;
        flex: none;
        order: initial;
      }

      .frisbuy-popup-content__bottom-layer--video-playing {
        background: linear-gradient(
          180deg,
          rgba(33, 33, 33, 0) 0%,
          #212121 100%
        );
        padding-bottom: var(--frisbuy-mobile-video-interface-height);
      }

      .frisbuy-popup-content__image {
        position: relative;
        z-index: 1;
        width: 100%;
        min-width: 0;
        height: calc(100dvh - 42px);
        min-height: 0;
        max-height: none;
        border-radius: 0;
        overflow: hidden;
      }

      .frisbuy-popup-content__media-counter {
        display: none;
      }

      .frisbuy-popup-content__info {
        display: block;
        position: relative;
        z-index: 1;
        left: auto;
        right: auto;
        bottom: auto;
        width: 100%;
        min-width: 0;
        height: auto;
        max-height: none;
        overflow: hidden;
        padding: 0;
        transition: max-height 0.2s ease;
        background: transparent;
      }

      .frisbuy-popup-content__info--video-interface-visible {
        max-height: calc(100% - var(--frisbuy-mobile-video-interface-height));
      }

      .frisbuy-popup-content__info--video-interface-visible
        .frisbuy-popup-content__modal-media,
      .frisbuy-popup-content__info--video-interface-visible
        :deep(.frisbuy-mobile-media-slider),
      .frisbuy-popup-content__info--video-interface-visible
        :deep(.frisbuy-modal-media-list) {
        display: none !important;
      }

      .frisbuy-popup-content__info--video-interface-visible
        :deep(.frisbuy-customer-review--modal-popup),
      .frisbuy-popup-content__info--video-interface-visible
        :deep(.frisbuy-customer-review.frisbuy-customer-review--modal-popup) {
        background: transparent !important;
        background-image: none !important;
      }

      .frisbuy-popup-content__modal-media {
        padding-top: 0;
        background: #1f2328;
      }

      .frisbuy-popup-content__open-review--inline {
        display: inline-flex;
        border: 0;
        border-bottom: 1px dashed #ffffff;
        background: transparent;
        color: rgba(0, 185, 191, 1);
        font-size: 14px;
        line-height: 20px;
        font-weight: 700;
        padding: 0 0 2px;
        margin-left: 0;
        align-self: center;
        white-space: nowrap;
      }

      .frisbuy-popup-content__info
        :deep(.frisbuy-customer-review__top-section) {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        flex-wrap: nowrap;
      }

      .frisbuy-popup-content__info :deep(.frisbuy-review-author--popup) {
        flex: 1 1 auto;
        min-width: 0;
      }

      .frisbuy-popup-content__info
        :deep(.frisbuy-customer-review--modal-popup) {
        height: auto !important;
        min-height: 0 !important;
        color: #ffffff;
        background: linear-gradient(
          180deg,
          rgba(33, 33, 33, 0) 0%,
          #212121 100%
        );
      }

      .frisbuy-popup-content__info
        :deep(
          .frisbuy-customer-review--modal-popup
            .frisbuy-customer-review__content-main-modal-popup.frisbuy-customer-review__content-main-modal-popup--collapsed
        ) {
        max-height: none;
        overflow: visible;
      }

      .frisbuy-popup-content__info
        :deep(
          .frisbuy-customer-review--modal-popup
            .frisbuy-customer-review__content-main-modal-popup.frisbuy-customer-review__content-main-modal-popup--collapsed::after
        ) {
        display: none;
      }

      .frisbuy-popup-content__info
        :deep(
          .frisbuy-customer-review--modal-popup
            .frisbuy-customer-review__read-more
        ),
      .frisbuy-popup-content__info
        :deep(
          .frisbuy-customer-review--modal-popup
            .frisbuy-customer-review__read-more-gradient
        ) {
        display: none;
      }

      .frisbuy-popup-content__info
        :deep(.frisbuy-review-author--popup .frisbuy-review-author__name),
      .frisbuy-popup-content__info :deep(.frisbuy-customer-review__text) {
        color: #ffffff;
      }

      .frisbuy-popup-content__info :deep(.frisbuy-review-author--popup) {
        align-items: center;
        gap: 8px;
        justify-content: flex-start;
      }

      .frisbuy-popup-content__info
        :deep(.frisbuy-review-author--popup .frisbuy-review-author__avatar) {
        display: none;
      }

      .frisbuy-popup-content__info
        :deep(.frisbuy-review-author--popup .frisbuy-review-author__main) {
        gap: 0;
      }

      .frisbuy-popup-content__info
        :deep(.frisbuy-review-author--popup .frisbuy-review-author__meta) {
        flex-direction: row;
        align-items: center;
        gap: 8px;
      }

      .frisbuy-popup-content__info
        :deep(.frisbuy-review-author--popup .frisbuy-review-author__date) {
        display: none;
      }

      .frisbuy-popup-content__info
        :deep(.frisbuy-review-author__rating-simple) {
        color: #ffffff;
        align-self: center;
      }

      .frisbuy-popup-content__info
        :deep(.frisbuy-review-author__rating-simple svg path) {
        fill: #ffffff;
      }

      .frisbuy-popup-content__info :deep(.frisbuy-customer-review__text) {
        margin-top: 8px;
        font-size: 14px;
        line-height: 22px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .frisbuy-popup-content__info
        :deep(.frisbuy-customer-review__text--positive),
      .frisbuy-popup-content__info
        :deep(.frisbuy-customer-review__text--negative) {
        display: inline;
      }

      .frisbuy-popup-content__info
        :deep(.frisbuy-customer-review__text--comment) {
        display: inline;
      }

      .frisbuy-popup-content--video-playing
        .frisbuy-popup-content__info
        :deep(.frisbuy-customer-review--modal-popup),
      .frisbuy-popup-content--video-playing
        .frisbuy-popup-content__info
        :deep(.frisbuy-customer-review.frisbuy-customer-review--modal-popup),
      .frisbuy-popup-content--video-playing
        .frisbuy-popup-content__info
        :deep(.frisbuy-customer-review__content-main-modal-popup),
      .frisbuy-popup-content--video-playing
        :deep(.frisbuy-mobile-video-controls) {
        background: transparent !important;
        background-image: none !important;
      }

      .frisbuy-popup-content__info :deep(.frisbuy-modal-media-list) {
        width: 100%;
        max-width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
        display: flex;
        flex-wrap: nowrap;
        padding: 4px 0 24px 16px;
      }
    }
  }
}
</style>

<script setup lang="ts">
// Modules
import {
  ref,
  watch,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
// Components
import PopupContainer from "../PopupContainer/PopupContainer.vue";
import CustomerReview from "../CustomerReview.vue";
import MediaList from "../MediaList.vue";
import MobileReviewSheet from "../MobileReviewSheet.vue";
import PopupReviewMediaList from "../PopupReviewMediaList.vue";
import PopupReviewMobileMediaSlider from "../PopupReviewMobileMediaSlider.vue";
import PopupReviewMobileVideoControls from "./PopupReviewMobileVideoControls.vue";
// Services
import { MediaCatcher } from "@/common/services/ErrorHandler/ErrorCatcher";
import { DomainSubstitute } from "@/common/services/DomainSubstitute/DomainSubstitute";
// Consts
import widgetTypes from "@/common/consts/widgetTypes";
// Types
import { TSwipe } from "../../globals";
import { imageWithPostId, IMarketplaceReviewsPost } from "../../interfaces";

const props = withDefaults(
  defineProps<{
    post: IMarketplaceReviewsPost;
    selectedPostIndex: number;
    selectedMediaIndex: number;
    showLeftArrow: boolean;
    showRightArrow: boolean;

    settings: { [key: string]: any };

    allCustomersMediaList?: Array<imageWithPostId>;
    totalMediaCount?: number;

    reviewDate: string;
    reviewTime: string;
  }>(),
  {
    showLeftArrow: true,
    showRightArrow: true,
    allCustomersMediaList: () => [],
  }
);

const emit = defineEmits([
  "selectPrevPost",
  "selectNextPost",
  "close",
  "selectPost",
  "requestToLoadCustomersMedia",
  "sendAnaliticEvent",
]);

const currentMediaIndex = ref<number>(props.selectedMediaIndex);
const swiperMediaList = computed<imageWithPostId[]>(() => {
  if (props.allCustomersMediaList.length) {
    return props.allCustomersMediaList;
  }

  return props.post.media.map((media) => ({
    thumbnailUrl: media.thumbnailUrl,
    imageUrl: media.imageUrl,
    imageHighResolutionUrl: media.imageHighResolutionUrl,
    videoUrl: media.videoUrl,
    postId: media.postId,
  }));
});
const popupMediaCount = computed<number>(() => {
  return props.totalMediaCount ?? swiperMediaList.value.length;
});
const isMediaNavigationVisible = computed<boolean>(
  () => popupMediaCount.value > 1
);
const canSlidePrev = computed<boolean>(() => {
  return isMediaNavigationVisible.value && currentMediaIndex.value > 0;
});
const canSlideNext = computed<boolean>(() => {
  return (
    isMediaNavigationVisible.value &&
    currentMediaIndex.value < popupMediaCount.value - 1
  );
});
const hasMoreCustomersMedia = computed<boolean>(() => {
  if (typeof props.totalMediaCount !== "number") {
    return true;
  }

  return props.allCustomersMediaList.length < props.totalMediaCount;
});
const isMobileReviewSheetOpen = ref(false);
const isPopupReadMoreExpanded = ref(false);
const isMobileViewport = ref(false);
const swiperInstance = ref<any | null>(null);
const isSwiperSyncing = ref(false);
const isSelectingFromSwiper = ref(false);
const PRELOAD_MEDIA_BEFORE_END = 2;
const didRequestAtSwiperEnd = ref(false);

function updateViewportMode() {
  isMobileViewport.value = window.innerWidth <= 1024;
}

const currentMedia = computed<imageWithPostId | undefined>(() => {
  return swiperMediaList.value[currentMediaIndex.value];
});
const currentPostMediaThumbnailUrl = computed<string | undefined>(() => {
  const currentPostId =
    props.post.instagramPost?.frisbuyId ?? props.post.providerPost?.frisbuyId;
  const globalMedia = currentMedia.value;

  if (globalMedia?.postId === currentPostId) {
    return globalMedia.thumbnailUrl;
  }

  const localMedia = props.post.media[props.selectedMediaIndex];
  return localMedia?.thumbnailUrl;
});
const isCurrentMediaVideo = computed<boolean>(() => {
  return Boolean(currentMedia.value?.videoUrl?.length);
});
const isMobileMediaHiddenAfterVideoStart = ref(false);
const isMobileMediaVisible = computed<boolean>(() => {
  if (!isMobileViewport.value) return false;
  if (!isMobileMediaHiddenAfterVideoStart.value) return true;
  return !isCurrentMediaVideo.value;
});
const videoElements = ref<(HTMLVideoElement | null)[]>([]);
const videoElement = computed<HTMLVideoElement | null>(() => {
  return videoElements.value[currentMediaIndex.value] || null;
});
const isVideoStarted = ref(false);
const isVideoPlaying = ref(false);

function setVideoRef(el: HTMLVideoElement | null, index: number) {
  videoElements.value[index] = el;
}

watch(
  () => [props.post, props.selectedMediaIndex, props.allCustomersMediaList],
  () => {
    if (isSelectingFromSwiper.value) {
      isSelectingFromSwiper.value = false;
      return;
    }

    const currentPostId =
      props.post.instagramPost?.frisbuyId ?? props.post.providerPost?.frisbuyId;
    const currentPostMedia = props.post.media[props.selectedMediaIndex];
    const targetMediaIndex = resolveGlobalMediaIndex(
      currentPostId,
      props.selectedMediaIndex,
      currentPostMedia
    );

    currentMediaIndex.value = targetMediaIndex >= 0 ? targetMediaIndex : 0;

    isPopupReadMoreExpanded.value = false;
    isVideoStarted.value = false;
    isVideoPlaying.value = false;

    // Miniatures become available again only after switching to an image slide.
    if (!isCurrentMediaVideo.value) {
      isMobileMediaHiddenAfterVideoStart.value = false;
    }

    nextTick(() => {
      if (!swiperInstance.value) return;
      if (swiperInstance.value.activeIndex === currentMediaIndex.value) return;

      isSwiperSyncing.value = true;
      swiperInstance.value.slideTo(currentMediaIndex.value, 0);
    });

    checkNeedLoadBySelectedMedia();
  },
  { immediate: true }
);

function selectPrevPost() {
  emit("selectPrevPost");
}

function selectNextPost() {
  emit("selectNextPost");
}

function selectPrevMedia() {
  if (!swiperInstance.value || !canSlidePrev.value) return;
  swiperInstance.value.slidePrev();
}

function selectNextMedia() {
  if (!swiperInstance.value || !canSlideNext.value) return;
  swiperInstance.value.slideNext();
}

function swipePost(direction: TSwipe) {
  if (direction === "left") {
    selectNextPost();
  }

  if (direction === "right") {
    selectPrevPost();
  }
}

function onSwiperInit(swiper: any) {
  swiperInstance.value = swiper;
}

function onSlideChange(swiper: any) {
  currentMediaIndex.value = swiper.activeIndex || 0;

  if (isVideoStarted.value) {
    stopVideo();
  }

  if (isSwiperSyncing.value) {
    isSwiperSyncing.value = false;
    return;
  }

  const selectedMedia = swiperMediaList.value[currentMediaIndex.value];
  if (!selectedMedia) return;

  const targetPostId = resolveMediaPostId(selectedMedia);
  if (targetPostId < 0) {
    return;
  }

  isSelectingFromSwiper.value = true;
  selectPost(targetPostId, selectedMedia);
  checkNeedLoadBySelectedMedia();
}

function shouldPreloadBySelectedMedia() {
  if (!swiperMediaList.value.length) {
    return false;
  }

  return (
    currentMediaIndex.value >=
    swiperMediaList.value.length - 1 - PRELOAD_MEDIA_BEFORE_END
  );
}

function checkNeedLoadBySelectedMedia() {
  if (!hasMoreCustomersMedia.value) {
    didRequestAtSwiperEnd.value = false;
    return;
  }

  const isNearEnd = shouldPreloadBySelectedMedia();

  if (isNearEnd && !didRequestAtSwiperEnd.value) {
    didRequestAtSwiperEnd.value = true;
    emit("requestToLoadCustomersMedia");
    return;
  }

  if (!isNearEnd) {
    didRequestAtSwiperEnd.value = false;
  }
}

function closePopup() {
  emit("close");
}

function selectMedia(media: imageWithPostId) {
  if (isVideoPlaying.value) {
    stopVideo();
  }

  const localMediaIndex = findLocalMediaIndex(media);
  const postId = resolveMediaPostId(media);
  if (postId < 0) {
    return;
  }

  selectPost(postId, media, localMediaIndex);
}

function onModalMediaSelect(media: imageWithPostId) {
  const localMediaIndex = findLocalMediaIndex(media);
  const targetPostId = getCurrentPostId(media.postId);
  const targetGlobalIndex = resolveGlobalMediaIndexByPostAndLocalIndex(
    targetPostId,
    Math.max(localMediaIndex, 0)
  );

  if (!swiperInstance.value || targetGlobalIndex < 0) {
    selectMedia(media);
    return;
  }

  if (swiperInstance.value.activeIndex === targetGlobalIndex) {
    return;
  }

  swiperInstance.value.slideTo(targetGlobalIndex);
}

function playVideo() {
  if (!videoElement.value) {
    console.warn("videoElement is 'null'");
    return;
  }

  emit("sendAnaliticEvent", "PlayVideo", {
    provPostId:
      props.post.instagramPost?.frisbuyId ?? props.post.providerPost.frisbuyId,
    fbProduct: props.post.frisbuyPost.id,
    igCode: props.post.instagramPost.shortcode,
  });

  isVideoPlaying.value = true;
  isVideoStarted.value = true;
  isMobileMediaHiddenAfterVideoStart.value = true;

  videoElement.value.play().catch(() => {
    isVideoPlaying.value = false;
  });
}

function toggleVideoPlayback() {
  if (!videoElement.value) {
    return;
  }

  if (videoElement.value.paused) {
    isVideoPlaying.value = true;
    videoElement.value.play().catch(() => {
      isVideoPlaying.value = false;
    });
    return;
  }

  pauseVideo();
}

function onVideoClick() {
  toggleVideoPlayback();
}

function onVideoPlay() {
  isVideoStarted.value = true;
  isVideoPlaying.value = true;
}

function onVideoPause() {
  isVideoPlaying.value = false;
}

function pauseVideo() {
  if (!videoElement.value) {
    console.warn("videoElement is 'null'");
    return;
  }

  isVideoPlaying.value = false;

  videoElement.value.pause();
}

function stopVideo() {
  if (!videoElement.value) {
    console.warn("videoElement is 'null'");
    return;
  }

  isVideoStarted.value = false;
  isVideoPlaying.value = false;

  videoElement.value.pause();
  videoElement.value.currentTime = 0;
}

function normalizeMediaUrl(url?: string) {
  if (!url) return "";

  return url
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\/[^/]+/i, "")
    .replace(/[?#].*$/, "");
}

function resolveMediaPostId(media: imageWithPostId): number {
  const directPostId = Number(media.postId);
  if (Number.isFinite(directPostId) && directPostId > 0) {
    return directPostId;
  }

  const mediaThumb = normalizeMediaUrl(media.thumbnailUrl);
  const mediaHighRes = normalizeMediaUrl(media.imageHighResolutionUrl);
  const mediaImage = normalizeMediaUrl(media.imageUrl);
  const mediaVideo = normalizeMediaUrl(media.videoUrl);

  const matchedGlobalMedia = props.allCustomersMediaList.find((item) => {
    const itemThumb = normalizeMediaUrl(item.thumbnailUrl);
    const itemHighRes = normalizeMediaUrl(item.imageHighResolutionUrl);
    const itemImage = normalizeMediaUrl(item.imageUrl);
    const itemVideo = normalizeMediaUrl(item.videoUrl);

    return (
      (mediaThumb && itemThumb && mediaThumb === itemThumb) ||
      (mediaHighRes && itemHighRes && mediaHighRes === itemHighRes) ||
      (mediaImage && itemImage && mediaImage === itemImage) ||
      (mediaVideo && itemVideo && mediaVideo === itemVideo)
    );
  });

  const matchedPostId = Number(matchedGlobalMedia?.postId);
  if (Number.isFinite(matchedPostId) && matchedPostId > 0) {
    return matchedPostId;
  }

  const currentPostId = Number(
    props.post.instagramPost?.frisbuyId ?? props.post.providerPost?.frisbuyId
  );
  return Number.isFinite(currentPostId) && currentPostId > 0
    ? currentPostId
    : -1;
}

// Customers photo
function selectPost(
  postId: number,
  media?: imageWithPostId,
  localMediaIndex?: number
) {
  if (isVideoStarted.value) {
    stopVideo();
  }

  emit("selectPost", postId, media, localMediaIndex);
}

// Customers photo loading on scroll
function loadCustomersMediaOnScroll(scrollPos: {
  top: number;
  bottom: number;
}) {
  if (scrollPos.bottom <= 0 && hasMoreCustomersMedia.value) {
    emit("requestToLoadCustomersMedia");
  }
}

// MediaList items size
onMounted(() => {
  updateViewportMode();
  window.addEventListener("resize", updateViewportMode);
  changeAllCustomersMediaListItemsWidth();
  window.addEventListener("resize", changeAllCustomersMediaListItemsWidth);

  changeReviewMediaListItemsWidth();
  window.addEventListener("resize", changeReviewMediaListItemsWidth);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateViewportMode);
  window.removeEventListener("resize", changeAllCustomersMediaListItemsWidth);
  window.removeEventListener("resize", changeReviewMediaListItemsWidth);
});

const reviewMediaListItemsWidth = ref<string>();
function changeReviewMediaListItemsWidth() {
  let itemsWidth = "65px";

  if (document.body.clientWidth <= 1024) {
    itemsWidth = "87px";
  }

  reviewMediaListItemsWidth.value = itemsWidth;
}

let allCustomersMediaListItemsWidth = ref<string>();
function changeAllCustomersMediaListItemsWidth() {
  let gap = "5px";
  let itemsInRow = 6;

  if (document.body.clientWidth <= 767) {
    itemsInRow = 2;
  } else if (document.body.clientWidth <= 1024) {
    itemsInRow = 3;
  } else {
    itemsInRow = 6;
  }

  allCustomersMediaListItemsWidth.value = `calc(1 / ${itemsInRow} * 100% - calc(${gap} * ${
    (itemsInRow - 1) / itemsInRow
  })`;
}

function errorImageHandler(e: Event) {
  MediaCatcher(e, 2, widgetTypes.STORY);
}

function resolveGlobalMediaIndex(
  postId: number,
  localMediaIndex: number,
  media?: imageWithPostId
) {
  if (!swiperMediaList.value.length) return -1;

  const samePostIndexes: number[] = [];
  swiperMediaList.value.forEach((item, idx) => {
    if (String(item.postId) === String(postId)) {
      samePostIndexes.push(idx);
    }
  });

  if (samePostIndexes.length) {
    const indexByLocal = samePostIndexes[localMediaIndex];
    if (typeof indexByLocal === "number") {
      return indexByLocal;
    }
  }

  if (media) {
    const exactIndex = swiperMediaList.value.findIndex((item) => {
      if (String(item.postId) !== String(postId)) return false;

      return (
        item.thumbnailUrl === media.thumbnailUrl ||
        item.imageHighResolutionUrl === media.imageHighResolutionUrl ||
        item.imageUrl === media.imageUrl ||
        item.videoUrl === media.videoUrl
      );
    });

    if (exactIndex >= 0) {
      return exactIndex;
    }
  }

  if (!samePostIndexes.length) return -1;
  return samePostIndexes[localMediaIndex] ?? samePostIndexes[0];
}

function resolveGlobalMediaIndexByPostAndLocalIndex(
  postId: number,
  localMediaIndex: number
) {
  const samePostIndexes: number[] = [];

  swiperMediaList.value.forEach((item, idx) => {
    if (String(item.postId) === String(postId)) {
      samePostIndexes.push(idx);
    }
  });

  if (!samePostIndexes.length) return -1;
  return samePostIndexes[localMediaIndex] ?? samePostIndexes[0];
}

function getCurrentPostId(overridePostId?: number) {
  return (
    overridePostId ||
    props.post.instagramPost?.frisbuyId ||
    props.post.providerPost?.frisbuyId
  );
}

function findLocalMediaIndex(media: imageWithPostId) {
  const directIndex = props.post.media.indexOf(media as any);
  if (directIndex >= 0) {
    return directIndex;
  }

  return props.post.media.findIndex((postMedia) => {
    return (
      (postMedia.thumbnailUrl &&
        media.thumbnailUrl &&
        postMedia.thumbnailUrl === media.thumbnailUrl) ||
      (postMedia.imageHighResolutionUrl &&
        media.imageHighResolutionUrl &&
        postMedia.imageHighResolutionUrl === media.imageHighResolutionUrl) ||
      (postMedia.imageUrl &&
        media.imageUrl &&
        postMedia.imageUrl === media.imageUrl) ||
      (postMedia.videoUrl &&
        media.videoUrl &&
        postMedia.videoUrl === media.videoUrl)
    );
  });
}
</script>
